import { Base } from '@studiometa/js-toolkit';
import { Frame, Menu, Action, Accordion, DataModel, DataEffect } from '@studiometa/ui';
import { nextTick } from '@studiometa/js-toolkit/utils';
import PriceRange from './PriceRange.js';
import Panel from '../Panel.js';
import StickyBar from './StickyBar.js';
import PanelFilters from '../PanelFilters.js';

/**
 * Facets component
 */
export default class Facets extends Base {
  /**
   * Component Configuration
   */
  static config = {
    name: 'Facets',
    refs: ['directinput', 'loadmore', 'container', 'products[]'],
    components: {
      Frame,
      Action,
      Menu,
      Panel,
      PanelFilters,
      PanelAccordion: Accordion,
      DataModel,
      DataEffect,
      PriceRange,
      StickyBar,
    },
    options: {
      productCount: {
        type: Number,
        default: 0,
      },
    },
  };

  /**
   * Get total products in listing
   * @returns {void}
   */
  get totalProducts() {
    return this.$refs.products.length;
  }

  /**
   * form
   * @returns {HTMLFormElement}
   */
  get form() {
    return this.$el.querySelector('form');
  }

  /**
   *
   */
  mounted() {
    this.setupScrollPosition();

    if (this.$refs.loadmore) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.loadMoreProducts();
            }
          });
        },
        {
          threshold: 1,
          rootMargin: '0px',
          root: null,
        },
      );

      observer.observe(this.$refs.loadmore);
    }
  }

  /**
   * onDirectinputChange
   */
  onDirectinputChange() {
    const event = new Event('submit', { bubbles: true, cancelable: false });
    this.form.dispatchEvent(event);
  }

  /**
   * Click on loadmore
   * @returns {void}
   */
  loadMoreProducts() {
    if (this.isLoading || this.isLast) return;

    const url = this.$refs.loadmore.dataset.href;
    this.isLoading = true;

    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const results = html.querySelector(`[data-ref="Facets.container"]`);

        nextTick(() => {
          this.$refs.container.innerHTML += results.innerHTML;
        });

        const newLoadmore = html.querySelector(`[data-ref="Facets.loadmore"]`);
        if (newLoadmore) {
          this.$refs.loadmore.dataset.href = newLoadmore.dataset.href;
        } else {
          this.isLast = true;
        }

        this.$root.$update();
        this.isLoading = false;

        setTimeout(() => {
          this.setupScrollPosition();
          if (this.targetScrollY) this.restoreScroll();
        }, 100);
      });
  }

  /**
   * Setup scroll position saving and restoration
   */
  setupScrollPosition() {
    this.$el.querySelectorAll('article[id] a[href*="/products/"]').forEach((link) => {
      link.addEventListener('click', () => {
        sessionStorage.setItem(
          'collection_scroll',
          JSON.stringify({
            scrollY: window.pageYOffset,
            productId: link.closest('article').id,
            path: window.location.pathname,
            time: Date.now(),
          }),
        );
      });
    });

    const data = sessionStorage.getItem('collection_scroll');
    if (data) {
      try {
        const { scrollY, productId, path, time } = JSON.parse(data);
        const isValid = path === window.location.pathname && Date.now() - time < 300000;

        if (isValid && scrollY > 0) {
          this.targetScrollY = scrollY;
          this.targetProductId = productId;
          sessionStorage.removeItem('collection_scroll');
          setTimeout(() => this.restoreScroll(), 100);
        }
      } catch {
        sessionStorage.removeItem('collection_scroll');
      }
    }
  }

  /**
   * Restore scroll to target position
   */
  restoreScroll() {
    if (!this.targetScrollY) return;

    const target = document.querySelector(`#${this.targetProductId}`);
    if (target) {
      window.scrollTo({
        top: Math.min(this.targetScrollY, target.offsetTop - 100),
        behavior: 'smooth',
      });
      this.targetScrollY = null;
    } else if (this.$refs.loadmore?.dataset.href && !this.isLoading) {
      this.loadMoreProducts(); // Load more to find target
    } else {
      window.scrollTo({ top: this.targetScrollY, behavior: 'smooth' });
      this.targetScrollY = null;
    }
  }
}
